import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Text,
  Button,
  styled,
  Stack,
  Divider,
} from '@ltvco/refresh-lib/theme';
import { nameize } from '@ltvco/refresh-lib/utils';
import { Record } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  CollectionType,
  PersonSearchParamsv2,
} from '@ltvco/refresh-lib/v2';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person,
  Tune,
} from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';

import { ReportChatCard } from 'components/ChatBV/components';
import {
  NUMBER_OF_TEASER_RESULTS_TO_SHOW,
  NUMBER_OF_TEASER_RESULTS_TO_SHOW_EXPANDED,
  CHATBV_GA4_EVENTS,
} from 'components/ChatBV/constants';
import { PersonParamsFunctionArguments } from 'components/ChatBV/types';
import { PersonFetcher } from 'routes/SearchPerson/Fetchers/PersonFetcher';

type MessageTeaserProps = {
  teaser: Record[];
  handleChatTeaserClick: (bvid: string) => void;
  currentBvid: string;
  reportOpen: boolean;
  searchData: AdvancedSearchURLParams;
  params?: PersonParamsFunctionArguments;
  handleViewAllResultsClick: () => void;
  handleFiltersClick: (params: PersonSearchParamsv2) => void;
};

const StyledStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const StyledText = styled(Text)(() => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const StyledSpan = styled('span')(() => ({
  fontWeight: 'bold',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  mt: 1,
  px: 2,
  color: theme.palette.text.secondary,
  borderColor: theme.palette.text.secondary,
}));

export const MessageTeaser = ({
  teaser,
  handleChatTeaserClick,
  currentBvid,
  reportOpen,
  searchData,
  params,
  handleViewAllResultsClick,
  handleFiltersClick,
}: MessageTeaserProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const [showMore, setShowMore] = useState(false);
  const [personResults, setPersonResults] = useState<Record[] | undefined>(
    teaser || []
  );
  const [selectedOption] = useState<CollectionType>(
    CollectionType.PUBLIC_RECORDS
  );
  const [localSearchData, setLocalSearchData] =
    useState<AdvancedSearchURLParams>(searchData);

  useEffect(() => {
    if (
      searchData?.fName === params?.first_name &&
      searchData?.lName === params?.last_name
    ) {
      setLocalSearchData(searchData);
    }
  }, [searchData, params?.first_name, params?.last_name]);

  const fetchers = () => {
    return (
      <>
        {selectedOption === CollectionType.PUBLIC_RECORDS && (
          <PersonFetcher
            setPersonResults={setPersonResults}
            searchData={localSearchData}
          />
        )}
      </>
    );
  };

  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  const handleChatPersonTeaserClick = (bvid: string) => {
    handleChatTeaserClick(bvid || '');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.person_card_click,
    });
  };

  return (
    <StyledStack divider={<Divider />}>
      <Stack spacing={2} pb={2}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Person />
          <Text variant="h3" sx={{ my: 0, mx: 1 }}>
            People
          </Text>
        </Box>
        {Boolean(params?.first_name || params?.last_name) && (
          <Text variant="h2">
            {`Results for: ${params?.first_name ?? ''} ${
              params?.last_name ?? ''
            }`.trim()}
          </Text>
        )}
        {params && (
          <Stack direction="row" justifyContent="space-between">
            <StyledButton
              variant="outlined"
              size="small"
              onClick={handleViewAllResultsClick}
            >
              View All Results
            </StyledButton>
            <StyledButton
              variant="outlined"
              size="small"
              onClick={() => handleFiltersClick(localSearchData)}
            >
              <Tune sx={{ mr: 1 }} />
              Filters
            </StyledButton>
          </Stack>
        )}
      </Stack>
      {(personResults?.length ? personResults : teaser)
        .slice(
          0,
          showMore
            ? NUMBER_OF_TEASER_RESULTS_TO_SHOW_EXPANDED
            : NUMBER_OF_TEASER_RESULTS_TO_SHOW
        )
        .map((item, index) => (
          <ReportChatCard
            title={item.name || ''}
            reportType="person"
            clickAction={() => handleChatPersonTeaserClick(item.bvid || '')}
            description={
              <Stack spacing={0.5}>
                {item.birthInfo && (
                  <StyledText color={'text.secondary'}>
                    <StyledSpan>Born:</StyledSpan>
                    {` ${item.birthInfo}`}
                  </StyledText>
                )}
                {item?.locations?.length ? (
                  <StyledText color={'text.secondary'}>
                    <StyledSpan>Locations:</StyledSpan>
                    {` ${item.locations
                      .filter((location) => Boolean(location))
                      .join(' • ')}`}
                  </StyledText>
                ) : null}
                {item.relatives?.length ? (
                  <StyledText color={'text.secondary'}>
                    <StyledSpan>Relatives:</StyledSpan>
                    {` ${item.relatives
                      .filter((relative) => Boolean(relative))
                      .map((relative) => nameize(relative))
                      .join(', ')}`}
                  </StyledText>
                ) : null}
                {item.aliases?.length ? (
                  <StyledText color={'text.secondary'}>
                    <StyledSpan>Aliases:</StyledSpan>
                    {` ${item.aliases
                      .filter((alias) => Boolean(alias))
                      .map((alias) => nameize(alias))
                      .join(', ')}`}
                  </StyledText>
                ) : null}
              </Stack>
            }
            disabled={currentBvid === item.bvid && reportOpen}
            key={item.bvid || `teaser-${index}`}
          />
        ))}
      {(personResults?.length ? personResults : teaser).length >
        NUMBER_OF_TEASER_RESULTS_TO_SHOW && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleShowMoreClick}
          sx={{ mt: 2 }}
        >
          {showMore ? 'Show Less' : 'Show More'}
          {showMore ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </Button>
      )}
      {fetchers()}
    </StyledStack>
  );
};
